import React, { useEffect, useState } from "react";
import { Button } from "../../Component";
import http from "../../../utils/http-common";
import { API_BASE_URL, findUpper } from "../../../utils/Utils";
import { formatAdminApiError } from "../../../utils/formatAdminApiError";
import swal from "sweetalert";

function assetUrl(relativeOrAbsolute) {
  const u = String(relativeOrAbsolute || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const base = String(API_BASE_URL || "").replace(/\/$/, "");
  const pathPart = u.startsWith("/") ? u : `/${u}`;
  return `${base}${pathPart}`;
}

/**
 * Admin upload/remove for ClinicPractitioner.photoUrl — reuses partner photo endpoints.
 */
const ProfilePhotoBlock = ({ clinicId, practitionerId, photoUrl, displayName, onChanged, disabled }) => {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(photoUrl || "");

  useEffect(() => {
    setPreview(photoUrl || "");
  }, [photoUrl, practitionerId]);

  const src = assetUrl(preview);
  const initials = findUpper(displayName || "P") || "P";

  const upload = async (file) => {
    if (!clinicId || !practitionerId || !file || disabled) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await http.post(
        `/api/v1/partners/medical/clinics/${clinicId}/practitioners/${practitionerId}/photo`,
        fd,
        { suppressInlineErrorSwal: true }
      );
      const next = res.data?.data?.photoUrl || "";
      setPreview(next || "");
      if (typeof onChanged === "function") onChanged(next || null);
    } catch (e) {
      swal("Upload failed", formatAdminApiError(e, "Could not upload photo"), "error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!clinicId || !practitionerId || disabled) return;
    const ok = await swal({
      title: "Remove profile photo?",
      text: "The public site will fall back to initials.",
      icon: "warning",
      buttons: ["Cancel", "Remove"],
      dangerMode: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await http.delete(
        `/api/v1/partners/medical/clinics/${clinicId}/practitioners/${practitionerId}/photo`,
        { suppressInlineErrorSwal: true }
      );
      setPreview("");
      if (typeof onChanged === "function") onChanged(null);
    } catch (e) {
      swal("Remove failed", formatAdminApiError(e, "Could not remove photo"), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="d-flex align-items-start gap-3 flex-wrap">
      <div
        className="flex-shrink-0 rounded-circle border overflow-hidden d-flex align-items-center justify-content-center bg-light"
        style={{ width: 72, height: 72 }}
        aria-hidden
      >
        {src ? (
          <img
            src={src}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span className="fw-bold text-soft" style={{ fontSize: 18 }}>
            {String(initials).slice(0, 2)}
          </span>
        )}
      </div>
      <div>
        <p className="small text-soft mb-2">
          Profile photo shown on the public catalog and doctor page. Stored on durable object storage.
        </p>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <label
            className={`btn btn-light btn-sm mb-0 ${busy || disabled ? "disabled" : ""}`}
            style={{ cursor: busy || disabled ? "not-allowed" : "pointer" }}
          >
            <input
              type="file"
              className="d-none"
              disabled={busy || disabled}
              accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp"
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                e.target.value = "";
                if (f) upload(f);
              }}
            />
            {busy ? "Saving…" : src ? "Replace photo" : "Upload photo"}
          </label>
          {src ? (
            <Button color="light" size="sm" disabled={busy || disabled} onClick={remove}>
              Remove
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ProfilePhotoBlock;
